import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { formatUsd } from '../../lib/format';
import { formatCarbonMass } from '../environmental/circularity/circularityFormat';
import { FlowSankey } from '../charts/FlowSankey';
import { buildScenarioCarbonTimeSankeySpec, buildScenarioValueTimeSankeySpec } from './scenarioTimeSankey';
import type { ScenarioCompareResponse } from '@siliconx/shared/dto/financials';

const SVG_WIDTH = 900;
const SVG_HEIGHT = 400;
const RIGHT_MARGIN = 200;

/**
 * SIL-517 — Compare step recap: two time-axis Sankeys unrolling each
 * cohort's decision path over the horizon (asset value, carbon), additive
 * below `ScenarioCompareChart`/`MetricTable` per spec ("narrative, not the
 * decision surface" — MetricTable stays the decision surface unchanged).
 * Built on the SAME generic `FlowSankey` engine SIL-516 shipped for
 * `LifecycleSankey`, with `colPos`/`colLabels` (time-proportional columns —
 * the feature SIL-516 added but its first consumer didn't use).
 *
 * Renders nothing when `byCohort` is absent/empty (older scenarios computed
 * before this field existed, or a scenario with zero cohorts) — additive
 * per spec Cross-cutting, never a broken diagram in place of "not
 * available yet".
 */
export function ScenarioTimeSankeys({ compareResult }: { compareResult: ScenarioCompareResponse }) {
  const byCohort = compareResult.byCohort ?? [];
  const useCarbonByCohort = compareResult.useCarbon?.byCohort ?? [];
  if (byCohort.length === 0) return null;

  const valueSpec = buildScenarioValueTimeSankeySpec(byCohort, compareResult.horizonMonths, formatUsd);
  const carbonSpec = buildScenarioCarbonTimeSankeySpec(
    byCohort,
    useCarbonByCohort,
    compareResult.horizonMonths,
    formatCarbonMass,
  );

  return (
    <div className="space-y-6">
      {valueSpec.nodes.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <SectionLabel className="mb-1">Asset value over time — decision paths by cohort</SectionLabel>
          <p className="mb-2 text-xs text-muted-foreground">
            Each lane tracks one cohort&apos;s FMV from today. It decays into a shared loss lane, then at the
            action month you either sell, ITAD, or refresh it — the write-off shown is what you actually
            didn&apos;t recover. Refresh capex is labeled on its source, not drawn to scale.
          </p>
          <FlowSankey
            {...valueSpec}
            width={SVG_WIDTH}
            height={SVG_HEIGHT}
            rightMargin={RIGHT_MARGIN}
            format={formatUsd}
            ariaLabel="Asset value flowing through time, one lane per cohort, diverging at each cohort's action month"
          />
        </div>
      )}

      {carbonSpec.nodes.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <SectionLabel className="mb-1">Carbon over time — decision paths by cohort</SectionLabel>
          <p className="mb-2 text-xs text-muted-foreground">
            Each lane accrues one cohort&apos;s use-phase carbon and freezes at its action month once
            post-action emissions aren&apos;t modeled. If you refresh, the new gear&apos;s embodied carbon
            lands as a one-time addition at that same month. Any avoided-vs-held comparison here is advisory
            only — don&apos;t sum it with the lanes.
          </p>
          <FlowSankey
            {...carbonSpec}
            width={SVG_WIDTH}
            height={SVG_HEIGHT}
            rightMargin={RIGHT_MARGIN}
            format={formatCarbonMass}
            ariaLabel="Carbon accrual flowing through time, one lane per cohort, freezing at each cohort's action month"
          />
        </div>
      )}
    </div>
  );
}
